import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "fj_access_token";
const REFRESH_KEY = "fj_refresh_token";

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  set: (access, refresh) => {
    if (access) localStorage.setItem(TOKEN_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const t = tokenStore.get();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const orig = err.config || {};
    // Auto-refresh on 401
    if (err.response?.status === 401 && !orig._retried && tokenStore.getRefresh()) {
      orig._retried = true;
      try {
        const { data } = await axios.post(`${API}/auth/refresh`, { refresh_token: tokenStore.getRefresh() });
        tokenStore.set(data.access_token, null);
        orig.headers = orig.headers || {};
        orig.headers.Authorization = `Bearer ${data.access_token}`;
        return api(orig);
      } catch (_e) {
        tokenStore.clear();
      }
    }
    // Retry-once on 502 for LLM endpoints (LiteLLM/upstream flakes)
    const isAiRoute = typeof orig.url === "string" && orig.url.includes("/ai-");
    if (err.response?.status === 502 && isAiRoute && !orig._retried502) {
      orig._retried502 = true;
      await new Promise((res) => setTimeout(res, 1200));
      return api(orig);
    }
    return Promise.reject(err);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Returns true when the API responded with 402 Payment Required (free-tier limit hit).
export function isFreemiumError(err) {
  return err?.response?.status === 402;
}

export const CURRENCY_SYMBOLS = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
  AUD: "A$",
  CAD: "C$",
  CHF: "CHF",
  INR: "₹",
};

export const formatMoney = (amount, currency = "USD") => {
  const sym = CURRENCY_SYMBOLS[currency] || "$";
  const num = Number(amount || 0);
  const sign = num < 0 ? "-" : "";
  return `${sign}${sym}${Math.abs(num).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};
